import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Droplets, Plus, Minus, Undo } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Water Glass Visualization Component
const WaterGlass = ({ percentage, totalOz, goalOz }) => {
  const fillHeight = Math.min(percentage, 100);
  
  return (
    <div className="flex flex-col items-center justify-center my-6">
      <div className="relative w-32 h-48">
        {/* Glass container */}
        <svg 
          viewBox="0 0 100 150" 
          className="w-full h-full"
          style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }}
        >
          {/* Glass body - tapered */}
          <defs>
            <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.95" />
            </linearGradient>
            <linearGradient id="glassShine" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.1)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
            </linearGradient>
          </defs>
          
          {/* Glass outline - trapezoid shape */}
          <path 
            d="M 30 10 L 20 140 L 80 140 L 70 10 Z" 
            fill="rgba(255, 255, 255, 0.1)"
            stroke="#60a5fa"
            strokeWidth="2"
          />
          
          {/* Water fill */}
          <clipPath id="glassClip">
            <path d="M 30 10 L 20 140 L 80 140 L 70 10 Z" />
          </clipPath>
          
          <g clipPath="url(#glassClip)">
            {/* Water level with wave effect */}
            <path
              d={`M 20 ${140 - (fillHeight * 1.3)} Q 35 ${138 - (fillHeight * 1.3)} 50 ${140 - (fillHeight * 1.3)} T 80 ${140 - (fillHeight * 1.3)} L 80 140 L 20 140 Z`}
              fill="url(#waterGradient)"
            />
            {/* Animated wave on top */}
            <ellipse
              cx="50"
              cy={140 - (fillHeight * 1.3)}
              rx="30"
              ry="3"
              fill="rgba(96, 165, 250, 0.5)"
              className="animate-pulse"
            />
          </g>
          
          {/* Glass shine effect */}
          <path 
            d="M 35 15 L 30 100" 
            stroke="url(#glassShine)"
            strokeWidth="8"
            strokeLinecap="round"
            opacity="0.6"
          />
          
          {/* Glass rim */}
          <line 
            x1="30" 
            y1="10" 
            x2="70" 
            y2="10" 
            stroke="#60a5fa" 
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        
        {/* Percentage text overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
              {Math.round(fillHeight)}%
            </p>
          </div>
        </div>
      </div>
      
      {/* Oz display below glass */}
      <div className="mt-2 text-center">
        <p className="text-lg font-semibold text-white">
          {totalOz} / {goalOz} oz
        </p>
        {fillHeight >= 100 && (
          <p className="text-sm text-green-400 font-semibold mt-1">
            🎉 Goal Achieved!
          </p>
        )}
      </div>
    </div>
  );
};

const HydrationTracker = ({ onComplete, promptId }) => {
  const [todayIntake, setTodayIntake] = useState({
    total_oz: 0,
    goal_oz: 64,
    progress_percentage: 0,
    goal_reached: false,
    weight_based_goal: false,
    user_weight: "Not specified"
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastAddedAmount, setLastAddedAmount] = useState(null);

  useEffect(() => {
    loadTodayIntake();
  }, []);

  const loadTodayIntake = async () => {
    try {
      setInitialLoading(true);
      const response = await axios.get(`${API}/api/water-intake/today`);
      setTodayIntake(response.data);
    } catch (error) {
      console.error('Failed to load water intake:', error);
      toast.error('Failed to load hydration data');
    } finally {
      setInitialLoading(false);
    }
  };

  const addWater = async (amount) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/water-intake/add`, {
        amount_oz: amount
      });
      
      // Update local state
      const newTotal = response.data.total_oz;
      const newGoal = response.data.goal_oz || todayIntake.goal_oz;
      const newProgressPercentage = Math.min((newTotal / newGoal) * 100, 100);
      
      setTodayIntake(prev => ({
        ...prev,
        total_oz: newTotal,
        goal_oz: newGoal,
        progress_percentage: newProgressPercentage,
        goal_reached: response.data.goal_reached
      }));

      // Store last added amount for undo functionality
      setLastAddedAmount(amount);
      
      // Show success message
      if (response.data.goal_reached && response.data.points_earned > 0) {
        toast.success(`${response.data.message} +${response.data.points_earned} points!`);
        
        // Handle badge notifications
        if (response.data.new_badges && response.data.new_badges.length > 0) {
          response.data.new_badges.forEach(badge => {
            setTimeout(() => {
              toast.success(`🏆 Badge Earned: ${badge}!`, { duration: 5000 });
            }, 1000);
          });
        }
      } else {
        toast.success(response.data.message);
      }
      
      // Automatically complete the daily hydration challenge when goal is reached (100%+)
      if (newProgressPercentage >= 100 && response.data.goal_reached) {
        const completionResponse = `Daily hydration tracking complete! Consumed ${newTotal} oz of water (${newProgressPercentage.toFixed(1)}% of ${newGoal} oz goal) - Goal achieved! 🎉`;
        await onComplete(promptId, completionResponse, 'hydration');
        toast.success('🎉 Hydration challenge completed for today!', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Failed to add water intake:', error);
      toast.error('Failed to add water intake');
    } finally {
      setLoading(false);
    }
  };

  const undoLastAdd = () => {
    if (lastAddedAmount && lastAddedAmount > 0) {
      addWater(-lastAddedAmount);
      setLastAddedAmount(null);
    }
  };

  if (initialLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse text-yellow-400">Loading hydration tracker...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 mb-4">
          <Droplets className="w-8 h-8 text-blue-400" />
          <h3 className="text-xl font-bold text-white">Daily Hydration Tracker</h3>
        </div>
        <p className="text-gray-200 text-sm">
          Stay hydrated for peak performance! Your personalized goal is based on your body weight.
        </p>
        {todayIntake.weight_based_goal && todayIntake.user_weight !== "Not specified" && (
          <p className="text-sky-300 text-xs mt-1">
            Goal calculated from your weight: {todayIntake.user_weight} ÷ 2 = {todayIntake.goal_oz} oz
          </p>
        )}
        {todayIntake.user_weight === "Not specified" && (
          <p className="text-yellow-300 text-xs mt-1">
            💡 Add your weight in your profile for a personalized hydration goal
          </p>
        )}
      </div>

      <Card className="bg-blue-900/70 border border-blue-700">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-white text-lg">Daily Hydration Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Water Glass Visualization */}
          <WaterGlass 
            percentage={todayIntake.progress_percentage}
            totalOz={todayIntake.total_oz}
            goalOz={todayIntake.goal_oz}
          />

          {/* Quick Add Buttons - Text based, professional */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              onClick={() => addWater(8)}
              disabled={loading}
              variant="outline"
              className="border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white h-16 flex flex-col items-center justify-center"
            >
              <span className="text-lg font-bold">+8 oz</span>
              <span className="text-xs opacity-75">Glass</span>
            </Button>

            <Button
              onClick={() => addWater(16)}
              disabled={loading}
              variant="outline"
              className="border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white h-16 flex flex-col items-center justify-center"
            >
              <span className="text-lg font-bold">+16 oz</span>
              <span className="text-xs opacity-75">Bottle</span>
            </Button>

            <Button
              onClick={() => addWater(40)}
              disabled={loading}
              variant="outline"
              className="border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white h-16 flex flex-col items-center justify-center"
            >
              <span className="text-lg font-bold">+40 oz</span>
              <span className="text-xs opacity-75">Stanley</span>
            </Button>
          </div>

          {/* Additional Options Row */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => addWater(4)}
              disabled={loading}
              variant="outline"
              size="sm"
              className="border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white"
            >
              +4 oz
            </Button>
            {lastAddedAmount && (
              <Button
                onClick={undoLastAdd}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-600 hover:text-black"
              >
                <Undo className="w-4 h-4 mr-1" />
                Undo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="p-3 bg-sky-900/50 rounded-lg border border-sky-400/40">
        <h4 className="text-sky-400 font-semibold mb-2 text-sm">💡 Hydration Tips</h4>
        <div className="text-xs text-gray-300 space-y-1">
          <p>• Drink 8 oz before, during, and after training sessions</p>
          <p>• Monitor urine color - aim for pale yellow</p>
          <p>• Increase intake in hot weather or intense training</p>
          <p>• Start hydrating as soon as you wake up</p>
        </div>
      </div>
    </div>
  );
};

export default HydrationTracker;